import React from "react";
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";

import { CompanyLogo } from "../shared";

/**
 * Invoice component to display invoice details
 *
 * @param {Object} props - Component props
 * @param {string} [props.title="Invoice Details"] - Title of the invoice
 * @param {Array} [props.sectionA=[]] - Left section of the invoice
 * @param {Array} [props.sectionB=[]] - Right section of the invoice
 * @param {Array} [props.sectionC=[]] - Section below header of the invoice
 * @param {Array} [props.sectionD=[]] - Section below header of the invoice
 * @param {Array} [props.columns=[]] - Columns of the table
 * @param {Array} [props.rows=[]] - Rows of the table
 * @param {any} [props.subtotal=null] - Subtotal of the invoice
 * @param {any} [props.discountAmount=null] - Discount amount of the invoice
 * @param {any} [props.total=null] - Total of the invoice
 *
 * @returns {JSX.Element} - Invoice component
 */
export const Invoice = ({
  title = "Invoice Details",
  sectionA = [],
  sectionB = [],
  sectionC = [],
  sectionD = [],
  columns = [],
  rows = [],
  subtotal = null,
  discountAmount = null,
  total = null,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      {/* Logo */}
      <CompanyLogo type="icon" style={{ width: 60, height: "auto", objectFit: "contain", marginBottom: "8px" }} plain={true} />

      {/* Title */}
      {title && (
        <Typography
          variant="h6"
          sx={{
            textAlign: "center",
            fontWeight: 600,
            textTransform: "uppercase",
            mb: 2,
          }}
        >
          {title}
        </Typography>
      )}

      {/* Header */}
      {(sectionA?.length > 0 || sectionB?.length > 0) && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            backgroundColor: "#F2ECF9",
            borderRadius: "10px",
            p: 1.5,
            mb: 2,
          }}
        >
          {/* Left */}
          {sectionA?.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                flex: "1 1 45%",
                minWidth: "250px",
                pr: 2,
              }}
            >
              {sectionA?.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    width: "100%",
                    alignItems: "flex-start",
                    gap: 1,
                    wordBreak: "break-word",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap", minWidth: "120px" }}>
                    {item.label}
                  </Typography>

                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    :
                  </Typography>

                  <Typography variant="body2" sx={{ flex: 1, overflowWrap: "break-word", wordBreak: "break-word" }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Right */}
          {sectionB?.length > 0 && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto",
                rowGap: 0.5,
                alignItems: "start",
                flex: "1 1 45%",
                minWidth: "250px",
                textAlign: "right",
                pl: 2,
              }}
            >
              {sectionB?.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 1,
                    wordBreak: "break-word",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {item.label} :
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: "60%",
                      textAlign: "right",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Section below header */}
      {(sectionC?.length > 0 || sectionD?.length > 0) && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            px: 1.5,
            mb: 2,
          }}
        >
          {/* Left */}
          {sectionC?.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                flex: "1 1 45%",
                minWidth: "250px",
                pr: 2,
              }}
            >
              {sectionC?.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1B1212",
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      wordBreak: "break-word",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Right */}
          {sectionD?.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                flex: "1 1 45%",
                minWidth: "250px",
                textAlign: "right",
                pl: 2,
              }}
            >
              {sectionD?.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1B1212",
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#444",
                      wordBreak: "break-word",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Table Section */}
      {(columns.length > 0 || rows.length > 0) && (
        <TableContainer component={Paper} sx={{ width: "100%", boxShadow: 0 }}>
          <Table
            sx={{
              borderCollapse: "collapse",
              "& td, & th": { padding: "10px 10px" },
            }}
          >
            {columns.length > 0 && (
              <TableHead>
                <TableRow>
                  {columns.map((col, index) => (
                    <TableCell
                      key={col.id || col.label}
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        borderTop: "1px solid #eeeeee",
                        borderRight: index !== columns.length - 1 ? "1px solid #eeeeee" : "none",
                        borderBottom: "1px solid #eeeeee",
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            )}

            <TableBody>
              {rows.length > 0 &&
                rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((col, colIndex) => (
                      <TableCell
                        key={col.id || col.label}
                        sx={{
                          borderRight: colIndex !== columns.length - 1 ? "1px solid #eeeeee" : "none",
                          borderBottom: "1px solid #eeeeee",
                        }}
                      >
                        {row[col.id] || "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Footer Section */}

      {subtotal && (
        <>
          <Box
            sx={{
              width: "100%",
              px: 1.5,
              mt: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Sub Total : ₹ {subtotal}
            </Typography>
          </Box>
          <Box sx={{ width: "100%", borderBottom: "1px solid #eeeeee", mt: 1 }} />
        </>
      )}

      {discountAmount && (
        <>
          <Box
            sx={{
              width: "100%",
              px: 1.5,
              mt: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Discount : ₹ {discountAmount}
            </Typography>
          </Box>
          <Box sx={{ width: "100%", borderBottom: "1px solid #eeeeee", mt: 1 }} />
        </>
      )}

      {total && (
        <>
          <Box
            sx={{
              width: "100%",
              px: 1.5,
              mt: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Total : ₹ {total}
            </Typography>
          </Box>
          <Box sx={{ width: "100%", borderBottom: "1px solid #eeeeee", mt: 1 }} />
        </>
      )}
    </Box>
  );
};
